import React, { useState, useEffect } from 'react';
import { CreditCard, Tag, CheckCircle, X, AlertCircle, Loader, Shield } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to safely extract error message from API responses
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return fallback;
};

const CashfreeCheckout = ({ 
  // Can pass either individual props or a team object
  team,
  amount: propAmount, 
  paymentType = 'team_registration', // 'team_registration', 'accommodation_booking'
  teamId: propTeamId,
  bookingId,
  teamName: propTeamName,
  userEmail: propUserEmail,
  userName: propUserName,
  userPhone: propUserPhone,
  onSuccess,
  onFailure,
  onClose,
  allowCoupon = true,
  feeType = 'hackathon_entry_fee', // For coupon validation
  buttonText = 'Pay Now',
  buttonClassName = ''
}) => {
  // Extract values from team object if provided, otherwise use individual props
  // Handle amount calculation with proper null/undefined checks
  const getAmount = () => {
    if (!team) return propAmount || 0;
    // For partial payments, use amount_due if it's greater than 0
    if (team.payment_status === 'partial' && team.amount_due > 0) {
      return team.amount_due;
    }
    // For pending payments, use total_price (which already includes any referral discount)
    if (team.payment_status === 'pending') {
      return team.total_price || 0;
    }
    // Fallback: use amount_due if set, otherwise total_price
    return (team.amount_due > 0 ? team.amount_due : team.total_price) || 0;
  };
  
  const amount = getAmount();
  const teamId = team?.id || propTeamId;
  const teamName = team?.team_name || propTeamName;
  const userEmail = team?.members?.[0]?.email || propUserEmail;
  const userName = team?.members?.[0]?.name || propUserName;
  const userPhone = team?.members?.[0]?.phone || propUserPhone;
  
  const [loading, setLoading] = useState(false);
  const [cashfreeConfig, setCashfreeConfig] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [showCouponInput, setShowCouponInput] = useState(false);

  const finalAmount = couponApplied ? couponApplied.final_amount : amount;

  useEffect(() => {
    fetchCashfreeConfig();
    loadCashfreeScript();
  }, []);

  const fetchCashfreeConfig = async () => {
    try {
      const response = await axios.get(`${API}/cashfree/config`);
      setCashfreeConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch Cashfree config:', err);
    }
  };

  const loadCashfreeScript = async () => {
    try {
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cashfreeInstance = await load({ mode: cashfreeConfig?.environment === 'PRODUCTION' ? 'production' : 'sandbox' });
      window._cashfreeInstance = cashfreeInstance;
      return true;
    } catch (err) {
      console.error('Failed to load Cashfree SDK:', err);
      return false;
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setApplyingCoupon(true);
    setCouponError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/coupons/apply`, {
        code: couponCode.toUpperCase(),
        fee_type: feeType,
        original_amount: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.valid) {
        setCouponApplied(response.data);
      }
    } catch (err) {
      setCouponError(getErrorMessage(err, 'Invalid coupon code'));
      setCouponApplied(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError('');
  };

  const handlePayment = async () => {
    // Validate required fields before proceeding
    if (!finalAmount || finalAmount <= 0) {
      if (onFailure) {
        onFailure({ error: 'Invalid payment amount. Please refresh and try again.' });
      }
      return;
    }
    
    if (!userName || !userEmail || !userPhone) {
      if (onFailure) {
        onFailure({ error: 'Missing customer details. Please ensure team information is complete.' });
      }
      return;
    }
    
    if (!teamId && paymentType === 'team_registration') {
      if (onFailure) {
        onFailure({ error: 'Team ID is required for registration payment.' });
      }
      return;
    }

    setLoading(true);
    
    try {
      // Load Cashfree script if not loaded
      const scriptLoaded = await loadCashfreeScript();
      if (!scriptLoaded) {
        throw new Error('Failed to load payment gateway. Please refresh the page.');
      }

      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Session expired. Please login again to continue.');
      }
      
      // Sanitize phone number - ensure it's 10 digits
      const sanitizedPhone = userPhone ? userPhone.replace(/\D/g, '').slice(-10) : '';
      if (sanitizedPhone.length !== 10) {
        throw new Error('Invalid phone number. Please update your team details with a valid 10-digit phone number.');
      }

      // Create order with Cashfree
      const orderResponse = await axios.post(`${API}/cashfree/create-order`, {
        amount: finalAmount,
        currency: 'INR',
        payment_type: paymentType,
        team_id: teamId,
        booking_id: bookingId,
        customer_name: userName || 'Customer',
        customer_email: userEmail,
        customer_phone: sanitizedPhone,
        notes: {
          team_name: teamName,
          coupon_applied: couponApplied?.code || null,
          original_amount: amount,
          discount: couponApplied?.discount_amount || 0,
          description: paymentType === 'team_registration' 
            ? `Team Registration - ${teamName}` 
            : `Booking Payment - ${teamName}`
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { order_id, payment_session_id } = orderResponse.data;

      if (!payment_session_id) {
        throw new Error('Failed to create payment session');
      }

      // Initialize Cashfree SDK
      const cashfree = window._cashfreeInstance;

      // Open Cashfree checkout
      const checkoutOptions = {
        paymentSessionId: payment_session_id,
        redirectTarget: '_modal', // Open in modal instead of redirect
      };

      cashfree.checkout(checkoutOptions).then(async (result) => {
        console.log('Cashfree checkout result:', result);
        
        // Defensive cleanup: Remove Cashfree modal container if it exists
        const cleanupCashfreeModal = () => {
          const modalContainer = document.getElementById('cashfree-modal-container');
          if (modalContainer) {
            modalContainer.remove();
          }
        };
        
        // Handle user cancellation or closing the payment modal
        if (!result || (!result.error && !result.paymentDetails && !result.redirect)) {
          console.log('Payment cancelled or closed by user');
          cleanupCashfreeModal();
          setLoading(false);
          if (onClose) {
            onClose();
          }
          return;
        }
        
        if (result.error) {
          console.error('Checkout error:', result.error);
          cleanupCashfreeModal();
          setLoading(false);
          if (onFailure) {
            onFailure({ error: result.error.message || 'Payment failed' });
          }
          if (onClose) {
            onClose();
          }
          return;
        }

        if (result.paymentDetails) {
          // Payment completed - verify on backend
          try {
            const verifyResponse = await axios.post(`${API}/cashfree/verify-payment`, {
              order_id: order_id,
              payment_type: paymentType,
              team_id: teamId,
              booking_id: bookingId
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            // Redeem coupon if applied
            if (couponApplied) {
              try {
                await axios.post(`${API}/coupons/redeem`, null, {
                  params: {
                    code: couponApplied.code,
                    fee_type: feeType,
                    amount_paid: finalAmount
                  },
                  headers: { Authorization: `Bearer ${token}` }
                });
              } catch (err) {
                console.error('Failed to redeem coupon:', err);
              }
            }

            if (onSuccess) {
              onSuccess(verifyResponse.data);
            }
          } catch (err) {
            console.error('Payment verification failed:', err);
            if (onFailure) {
              onFailure({ error: 'Payment verification failed. Please contact support.' });
            }
          }
          setLoading(false);
          return;
        }

        if (result.redirect) {
          // Payment is being processed via redirect
          // The return URL will handle the verification
          setLoading(false);
          return;
        }

        // Fallback - close modal if no recognized result
        cleanupCashfreeModal();
        setLoading(false);
        if (onClose) {
          onClose();
        }
      }).catch((err) => {
        console.error('Cashfree checkout error:', err);
        // Cleanup on error
        const modalContainer = document.getElementById('cashfree-modal-container');
        if (modalContainer) {
          modalContainer.remove();
        }
        setLoading(false);
        if (onFailure) {
          onFailure({ error: err.message || 'Payment failed' });
        }
        if (onClose) {
          onClose();
        }
      });

    } catch (err) {
      console.error('Payment initiation failed:', err);
      // Extract error message from various possible formats
      let errorMessage = 'Failed to initiate payment. Please try again.';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (onFailure) {
        onFailure({ error: errorMessage });
      }
      setLoading(false);
    }
  };

  const content = (
    <div className="space-y-4">
      {/* Coupon Section */}
      {allowCoupon && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
          {!showCouponInput && !couponApplied ? (
            <button
              onClick={() => setShowCouponInput(true)}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-2"
              data-testid="show-coupon-input"
            >
              <Tag size={16} />
              Have a coupon or referral code?
            </button>
          ) : couponApplied ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-600" size={18} />
                <span className="font-semibold text-green-700">{couponApplied.code}</span>
                <span className="text-green-600 text-sm">
                  ({couponApplied.discount_type === 'percentage' 
                    ? `${couponApplied.discount_value}% off` 
                    : `₹${couponApplied.discount_value} off`})
                </span>
              </div>
              <button
                onClick={removeCoupon}
                className="text-red-600 hover:text-red-700 p-1"
                data-testid="remove-coupon"
              >
                <X size={18} />
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:border-blue-500 font-mono text-sm"
                  data-testid="coupon-input"
                />
                <button
                  onClick={applyCoupon}
                  disabled={applyingCoupon || !couponCode.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  data-testid="apply-coupon-btn"
                >
                  {applyingCoupon ? 'Applying...' : 'Apply'}
                </button>
              </div>
              {couponError && (
                <p className="text-red-600 text-sm flex items-center gap-1">
                  <AlertCircle size={14} />
                  {couponError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Price Summary */}
      <div className="bg-white rounded-lg p-4 border-2 border-gray-200">
        <div className="space-y-2">
          <div className="flex justify-between text-gray-600">
            <span>Original Amount</span>
            <span className={couponApplied ? 'line-through' : ''}>₹{amount.toLocaleString()}</span>
          </div>
          
          {couponApplied && (
            <>
              <div className="flex justify-between text-green-600">
                <span>Discount ({couponApplied.code})</span>
                <span>- ₹{couponApplied.discount_amount.toLocaleString()}</span>
              </div>
              <hr className="my-2" />
            </>
          )}
          
          <div className="flex justify-between text-lg font-bold">
            <span>Total Amount</span>
            <span className="text-green-700">₹{finalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Pay Button */}
      <button
        onClick={handlePayment}
        disabled={loading || !cashfreeConfig}
        className={`w-full py-4 rounded-lg font-bold text-lg flex items-center justify-center gap-2 transition-all ${
          buttonClassName || 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white'
        } disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="pay-now-btn"
      >
        {loading ? (
          <>
            <Loader className="animate-spin" size={20} />
            Processing...
          </>
        ) : (
          <>
            <CreditCard size={20} />
            {buttonText} - ₹{finalAmount.toLocaleString()}
          </>
        )}
      </button>

      <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
        <Shield size={14} />
        <span>Secured by Cashfree • 100% Safe & Secure Payment</span>
      </div>
    </div>
  );

  // If onClose is provided, render as a modal
  if (onClose) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
          <div className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-t-xl">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">Complete Payment</h2>
                <p className="text-green-100 text-sm mt-1">{teamName || 'Team Registration'}</p>
              </div>
              <button 
                onClick={onClose} 
                className="text-white hover:text-green-200 p-1"
                data-testid="close-payment-modal"
              >
                <X size={24} />
              </button>
            </div>
          </div>
          <div className="p-6">
            {content}
          </div>
        </div>
      </div>
    );
  }

  // Otherwise render inline
  return content;
};

export default CashfreeCheckout;
